import { useCallback, useEffect, useRef, useState } from 'react';

export const CONNECTIVITY_STATUS = Object.freeze({
  CHECKING: 'checking',
  ONLINE: 'online',
  LIMITED: 'limited',
  OFFLINE: 'offline',
});

const browserHasNetwork = () => typeof navigator === 'undefined' || navigator.onLine !== false;

export default function useConnectivityStatus() {
  const [status, setStatus] = useState(() => (
    browserHasNetwork() ? CONNECTIVITY_STATUS.CHECKING : CONNECTIVITY_STATUS.OFFLINE
  ));
  const [lastCheckedAt, setLastCheckedAt] = useState(0);
  const probeRef = useRef(null);

  const checkConnectivity = useCallback(async () => {
    if (!browserHasNetwork()) {
      setStatus(CONNECTIVITY_STATUS.OFFLINE);
      setLastCheckedAt(Date.now());
      return false;
    }
    if (probeRef.current) return probeRef.current;

    const request = (async () => {
      setStatus((current) => current === CONNECTIVITY_STATUS.ONLINE ? current : CONNECTIVITY_STATUS.CHECKING);
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 4500);
      try {
        const response = await fetch(`/api/cash/open?health=1&t=${Date.now()}`, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal,
        });
        const body = await response.json().catch(() => null);
        const online = response.ok && body?.ok === true;
        setStatus(online ? CONNECTIVITY_STATUS.ONLINE : CONNECTIVITY_STATUS.LIMITED);
        return online;
      } catch (_error) {
        setStatus(browserHasNetwork() ? CONNECTIVITY_STATUS.LIMITED : CONNECTIVITY_STATUS.OFFLINE);
        return false;
      } finally {
        window.clearTimeout(timeoutId);
        setLastCheckedAt(Date.now());
        probeRef.current = null;
      }
    })();
    probeRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      setStatus(CONNECTIVITY_STATUS.OFFLINE);
      setLastCheckedAt(Date.now());
    };
    const handleOnline = () => { void checkConnectivity(); };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void checkConnectivity();
    };

    void checkConnectivity();
    const intervalId = window.setInterval(checkConnectivity, 30000);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkConnectivity]);

  return {
    status,
    isOnline: status === CONNECTIVITY_STATUS.ONLINE,
    lastCheckedAt,
    checkConnectivity,
  };
}
